import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    AlertTriangle,
    Calculator,
    Check,
    CheckCircle2,
    ChevronDown,
    CircleDollarSign,
    Copy,
    Database,
    Info,
    Layers3,
    Loader2,
    Package,
    Percent,
    Plus,
    RefreshCw,
    Save,
    Search,
    SlidersHorizontal,
    Sparkles,
    TestTube2,
    Trash2,
    X,
} from 'lucide-react';
import { getCargos, salvarCargo } from '../services/cargos';
import { getAppSetting, invalidateProdutosCache, setAppSetting, hojeSP } from '../services/api';
import {
    BONUS_TYPES,
    CASH_COLLECT_PRODUCTS,
    OFFICIAL_CASH_COLLECT_CARGO_KEYS,
    OFFICIAL_COMMISSION_VALID_FROM,
    PARTICIPANT_CONTEXTS,
    PAYMENT_CATEGORIES,
    buildOfficialCashCollectRules,
    calculateCommissionScenario,
    commissionCargoKey,
    commissionRuleCargoKey,
    getCommissionRulesConfig,
    officialManagerOverrideRateForCargo,
    officialSaleRateForCargo,
    saveCommissionRulesConfig,
} from '../services/commissionRules';
import { toNewConfig, verificarInvariantesCloser } from '../services/pontosConfig';
import './Comissionamento.css';

const RATE_FIELDS = [
    { key: 'comissao_agendamento', type: 'fixed' },
    { key: 'comissao_venda', type: 'percent' },
    { key: 'comissao_indicacao', type: 'percent' },
];

const MANAGER_PRODUCT_KEY = commissionCargoKey('Gestor Comercial');
const OFFICIAL_CASH_RULE_PREFIX = 'official-cash-collect-';
const VIEW_ALIASES = { plan: 'rules', rules: 'rules', rates: 'rates', simulator: 'simulator', origin: 'rules' };

const EMPTY_SIMULATION = {
    sellerCargoId: '',
    preSaleCargoId: '',
    productSlug: '',
    paymentCategory: 'a_vista',
    contractTotal: '',
    firstMonthCash: '',
    commissionBase: '',
    eventDate: hojeSP(),
    includeManagement: true,
};

function formatBRL(value) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value ?? 0));
}

function formatPercent(value) {
    return `${(Number(value ?? 0) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%`;
}

function displayRate(type, value) {
    const number = Number(value ?? 0);
    return String(type === 'percent' ? Number((number * 100).toFixed(6)) : number);
}

function persistedRate(type, value) {
    const number = Number.parseFloat(value) || 0;
    return type === 'percent' ? number / 100 : number;
}

function finiteDraftNumber(value) {
    if (value === '' || value == null) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function isValidIsoDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value ?? ''))) return false;
    const date = new Date(`${value}T12:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function isValidPercentDraft(value) {
    const number = finiteDraftNumber(value);
    return number != null && number >= 0 && number <= 100;
}

function isCashCollectRule(rule) {
    return String(rule?.id ?? '').startsWith(OFFICIAL_CASH_RULE_PREFIX)
        || String(rule?.exclusiveGroup ?? '').startsWith('cash-collect:');
}

function requireConfiguredProducts(value) {
    if (!Array.isArray(value) || value.length === 0) {
        throw new Error('O catálogo central de produtos não foi encontrado. Corrija app_settings.produtos_config e tente novamente.');
    }
    const invalidProduct = value.find((product) => (
        !String(product?.slug ?? '').trim()
        || !String(product?.label ?? '').trim()
        || !Array.isArray(product?.keywords)
        || product.keywords.some((keyword) => typeof keyword !== 'string')
    ));
    if (invalidProduct) throw new Error('Existe um produto sem slug, nome ou palavras-chave válidas no catálogo central.');
    const slugs = value.map((product) => String(product.slug).trim());
    if (new Set(slugs).size !== slugs.length) throw new Error('Existem slugs duplicados no catálogo central de produtos.');
    return value;
}

function buildRoleDraft(cargo) {
    const display = {};
    for (const field of RATE_FIELDS) display[field.key] = displayRate(field.type, cargo[`${field.key}_valor`]);
    display.comissao_venda_override = cargo.comissao_venda_override_valor == null
        ? ''
        : String(Number(cargo.comissao_venda_override_valor) * 100);
    return { ...cargo, display };
}

function roleLabel(role) {
    const key = commissionRuleCargoKey(role?.nome);
    if (key === 'gestor_comercial') return 'Gestor Comercial';
    if (key === 'sdr') return 'SDR';
    if (key === 'closer') return 'Closer';
    if (key === 'social_seller') return 'Social Seller';
    return role?.nome || 'Cargo';
}

function roleRateNote(role) {
    const key = commissionRuleCargoKey(role?.nome);
    if (key === 'gestor_comercial') return 'Venda própria soma produção + gestão';
    if (key === 'sdr') return 'Participação de pré-venda';
    if (key === 'social_seller') return 'Venda de número que ele coletou';
    return 'Vendedor responsável';
}

/**
 * Chave da exceção por produto. `commissionCargoKey` mapeia "Social Seller" para `sdr`, mas a
 * exceção de SDR é remuneração da pré-venda; o motor lê a do Social Seller pela chave própria
 * (`commissionRuleCargoKey` → `social_seller`, ver `submitVenda`). Os demais cargos continuam
 * nas chaves de sempre (`closer`, `sdr`, `gestor_comercial`), que é o que a config já tem.
 */
function productRateKey(role) {
    const ruleKey = commissionRuleCargoKey(role?.nome);
    return ruleKey === 'social_seller' ? ruleKey : commissionCargoKey(role?.nome);
}

function ruleThreshold(rule, referencePrice, contractTotal = 0) {
    if (rule?.minCashAmount != null) return Number(rule.minCashAmount);
    const total = referencePrice ?? contractTotal;
    return total > 0 && rule?.minCashPercent != null ? total * Number(rule.minCashPercent) / 100 : 0;
}

function rateForParticipant(role, product, participantContext) {
    if (!role) return 0;
    const productRate = product?.comissoes?.[commissionCargoKey(role.nome)];
    if (productRate != null) return productRate;
    const draftValue = participantContext === 'manager'
        ? role.display.comissao_venda_override
        : role.display.comissao_venda;
    return persistedRate('percent', draftValue);
}

function rateCalculationLabel(result) {
    if (!result) return '';
    if (Math.abs(result.baseRate - result.effectiveRate) < 0.0000001) return formatPercent(result.effectiveRate);
    return `${formatPercent(result.baseRate)} → ${formatPercent(result.effectiveRate)}`;
}

function createRule({ mode = 'single', productSlug = '' } = {}) {
    const id = globalThis.crypto?.randomUUID?.() || `rule-${Date.now()}`;
    const tiered = mode === 'tier';
    return {
        id: `draft-${id}`,
        name: tiered ? 'Nova faixa de cash collect' : 'Nova bonificação',
        active: false,
        type: 'fixed',
        value: 0,
        cargoKeys: [],
        participantContexts: ['seller'],
        productSlugs: productSlug ? [productSlug] : [],
        paymentCategories: [],
        minCashPercent: tiered ? 0 : null,
        maxCashPercent: null,
        minCashAmount: null,
        maxCashAmount: null,
        validFrom: hojeSP(),
        validTo: null,
        priority: 100,
        exclusiveGroup: tiered ? `cash-collect:${productSlug}:draft-${id}` : null,
        exclusiveRank: tiered ? 0 : null,
    };
}

function validatePlanDraft({ roleDrafts, products, rules, productInvariants = { bloqueios: [] } }) {
    const errors = [];
    for (const role of roleDrafts) {
        for (const field of RATE_FIELDS) {
            const value = finiteDraftNumber(role.display?.[field.key]);
            if (value == null || value < 0 || (field.type === 'percent' && value > 100)) errors.push(`${roleLabel(role)} N${role.nivel ?? 1}: revise os valores do cargo.`);
        }
        const override = role.display?.comissao_venda_override;
        const requiresOverride = commissionRuleCargoKey(role.nome) === 'gestor_comercial';
        if ((requiresOverride || override !== '') && !isValidPercentDraft(override)) errors.push(`${roleLabel(role)} N${role.nivel ?? 1}: a participação de gestão deve estar entre 0% e 100%.`);
    }
    for (const product of products) {
        for (const [cargoKey, rate] of Object.entries(product.comissoes ?? {})) {
            const number = finiteDraftNumber(rate);
            if (number == null || number < 0 || number > 1) errors.push(`${product.label}: a exceção de ${cargoKey} é inválida.`);
        }
        // A chave `gestor_comercial` é ambígua de fato — ela não diz se sobrescreve a venda
        // própria do gestor ou a participação de gestão (bd_cargos separa em comissao_venda_valor
        // e comissao_venda_override_valor; aqui é uma chave só). Mas ela NÃO é inválida: em
        // produção ela paga. A venda de PCL de 27/06 gerou R$ 79,94 de gestão sobre R$ 3.997,
        // exatamente os 2% de `treinamentos_orbion.comissoes.gestor_comercial`.
        //
        // Tratá-la como erro bloqueava a publicação INTEIRA — cargos, produtos e regras — e foi
        // por isso que o cash collect nunca subiu. A camada de config chamava de inválido o que
        // a camada de cálculo usava para pagar. Vira aviso até a desambiguação em duas chaves.
    }
    for (const blocker of productInvariants.bloqueios ?? []) errors.push(`Catálogo de produtos: ${blocker.resumo}`);
    for (const rule of rules.filter((item) => item.active)) {
        if (!String(rule.name ?? '').trim()) errors.push('Toda regra ativa precisa de um nome.');
        if (!BONUS_TYPES.some((type) => type.value === rule.type)) errors.push(`${rule.name || 'Regra'}: selecione um resultado válido.`);
        if (!(finiteDraftNumber(rule.value) > 0)) errors.push(`${rule.name || 'Regra'}: o benefício precisa ser maior que zero.`);
        if (rule.validFrom && !isValidIsoDate(rule.validFrom)) errors.push(`${rule.name || 'Regra'}: a data inicial é inválida.`);
        if (rule.validTo && !isValidIsoDate(rule.validTo)) errors.push(`${rule.name || 'Regra'}: a data final é inválida.`);
        if (rule.validFrom && rule.validTo && rule.validFrom > rule.validTo) errors.push(`${rule.name || 'Regra'}: a data final vem antes da inicial.`);
        const minPercent = finiteDraftNumber(rule.minCashPercent);
        const maxPercent = finiteDraftNumber(rule.maxCashPercent);
        const minAmount = finiteDraftNumber(rule.minCashAmount);
        const maxAmount = finiteDraftNumber(rule.maxCashAmount);
        if (minPercent != null && (minPercent < 0 || minPercent > 100)) errors.push(`${rule.name}: o percentual mínimo deve estar entre 0% e 100%.`);
        if (maxPercent != null && (maxPercent < 0 || maxPercent > 100)) errors.push(`${rule.name}: o percentual máximo deve estar entre 0% e 100%.`);
        if (minPercent != null && maxPercent != null && minPercent > maxPercent) errors.push(`${rule.name}: o percentual mínimo supera o máximo.`);
        if (minAmount != null && maxAmount != null && minAmount > maxAmount) errors.push(`${rule.name}: a entrada mínima supera a máxima.`);
        if (rule.exclusiveGroup?.startsWith('cash-collect:') && rule.productSlugs?.length !== 1) errors.push(`${rule.name}: uma faixa progressiva precisa estar ligada a um produto.`);
    }
    const tierGroupsByProduct = new Map();
    for (const rule of rules.filter((item) => item.active && isCashCollectRule(item))) {
        const productSlug = rule.productSlugs?.[0];
        if (!productSlug) continue;
        if (!tierGroupsByProduct.has(productSlug)) tierGroupsByProduct.set(productSlug, new Map());
        const groups = tierGroupsByProduct.get(productSlug);
        const groupName = rule.exclusiveGroup || rule.id;
        const current = groups.get(groupName);
        groups.set(groupName, current ? {
            from: !current.from || !rule.validFrom ? null : rule.validFrom < current.from ? rule.validFrom : current.from,
            to: !current.to || !rule.validTo ? null : rule.validTo > current.to ? rule.validTo : current.to,
        } : { from: rule.validFrom || null, to: rule.validTo || null });
    }
    for (const [productSlug, groups] of tierGroupsByProduct) {
        const entries = Array.from(groups.values());
        const overlaps = entries.some((first, index) => entries.slice(index + 1).some((second) => (
            (!first.to || !second.from || first.to >= second.from)
            && (!second.to || !first.from || second.to >= first.from)
        )));
        if (overlaps) errors.push(`${products.find((product) => product.slug === productSlug)?.label ?? productSlug}: existe mais de um conjunto progressivo ativo na mesma vigência. Desative ou encerre o anterior para evitar bônus em duplicidade.`);
    }
    return [...new Set(errors)];
}

function ruleResultLabel(rule) {
    if (rule.type === 'fixed') return `+ ${formatBRL(rule.value)}`;
    if (rule.type === 'multiplier') return `× ${Number(rule.value).toLocaleString('pt-BR')}`;
    return `+ ${Number(rule.value).toLocaleString('pt-BR')} p.p.`;
}

function ruleConditionLabel(rule, roleLabels, productLabels) {
    const parts = [];
    parts.push(rule.productSlugs?.length ? rule.productSlugs.map((key) => productLabels.get(key) ?? key).join(', ') : 'Todos os produtos');
    parts.push(rule.cargoKeys?.length ? rule.cargoKeys.map((key) => roleLabels.get(key) ?? key).join(', ') : 'Todos os cargos');
    if (rule.paymentCategories?.length) parts.push(rule.paymentCategories.map((key) => PAYMENT_CATEGORIES.find((item) => item.value === key)?.label ?? key).join(', '));
    if (rule.minCashPercent != null) parts.push(`a partir de ${Number(rule.minCashPercent).toLocaleString('pt-BR')}% de entrada`);
    if (rule.minCashAmount != null) parts.push(`a partir de ${formatBRL(rule.minCashAmount)}`);
    return parts.join(' · ');
}

function buildRuleInventory(rules, productLabels) {
    const groups = new Map();
    const items = [];
    for (const rule of rules) {
        if (!isCashCollectRule(rule)) {
            items.push({ key: `rule:${rule.id}`, kind: 'rule', rule });
            continue;
        }
        const groupName = rule.exclusiveGroup || `cash-collect:${rule.productSlugs?.[0] || rule.id}`;
        if (!groups.has(groupName)) groups.set(groupName, []);
        groups.get(groupName).push(rule);
    }
    for (const [groupName, groupRules] of groups) {
        groupRules.sort((a, b) => Number(a.exclusiveRank ?? a.minCashAmount ?? a.minCashPercent ?? 0) - Number(b.exclusiveRank ?? b.minCashAmount ?? b.minCashPercent ?? 0));
        const productSlug = groupRules[0]?.productSlugs?.[0] || groupName.split(':').slice(1).join(':');
        items.push({ key: `group:${groupName}`, kind: 'tier-group', groupName, productSlug, productLabel: productLabels.get(productSlug) ?? productSlug ?? 'Produto não definido', rules: groupRules });
    }
    return items.sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'tier-group' ? -1 : 1;
        const nameA = a.kind === 'tier-group' ? a.productLabel : a.rule.name;
        const nameB = b.kind === 'tier-group' ? b.productLabel : b.rule.name;
        return nameA.localeCompare(nameB, 'pt-BR');
    });
}

function Toggle({ checked, onChange, label, disabled = false }) {
    return <label className="commission-toggle"><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} disabled={disabled} /><span aria-hidden="true"><i /></span><b>{label}</b></label>;
}

function ChoiceGrid({ legend, options, values, onChange, allLabel }) {
    const allSelected = values.length === 0;
    function toggle(value) {
        onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
    }
    return (
        <fieldset className="commission-choice-fieldset">
            <legend>{legend}</legend>
            <div className="commission-choice-grid">
                <label className={allSelected ? 'selected' : ''}><input type="checkbox" checked={allSelected} onChange={() => onChange([])} /><span>{allLabel}</span></label>
                {options.map((option) => <label key={option.value} className={values.includes(option.value) ? 'selected' : ''}><input type="checkbox" checked={values.includes(option.value)} onChange={() => toggle(option.value)} /><span>{option.label}</span></label>)}
            </div>
        </fieldset>
    );
}

function RuleEditor({ rule, products, roleOptions, onPatch, onDuplicate, onDelete }) {
    if (!rule) return null;
    const setNullableNumber = (field, value) => onPatch({ [field]: value === '' ? null : Number(value) });
    return (
        <form className="commission-rule-editor" onSubmit={(event) => event.preventDefault()}>
            <header className="commission-editor-header">
                <div className="commission-editor-title"><span className="commission-editor-icon"><SlidersHorizontal size={18} /></span><div><span>Regra de bonificação</span><h2>{rule.name || 'Nova bonificação'}</h2></div></div>
                <Toggle checked={rule.active} onChange={(active) => onPatch({ active })} label={rule.active ? 'Ativa' : 'Inativa'} />
            </header>
            {!rule.active && <div className="commission-inline-note"><Info size={16} />Esta regra não participa dos cálculos enquanto estiver inativa.</div>}
            <div className="commission-editor-section">
                <div className="commission-editor-section-title"><span>Definição</span><small>Nome interno e valor aplicado quando as condições forem atendidas.</small></div>
                <div className="commission-form-grid commission-form-grid-2">
                    <label className="commission-field commission-field-wide"><span>Nome da regra</span><input value={rule.name} onChange={(event) => onPatch({ name: event.target.value })} placeholder="Ex.: Bônus à vista — Performance" /></label>
                    <label className="commission-field"><span>Resultado</span><select value={rule.type} onChange={(event) => onPatch({ type: event.target.value })}>{BONUS_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></label>
                    <label className="commission-field"><span>{rule.type === 'fixed' ? 'Valor do bônus' : rule.type === 'multiplier' ? 'Multiplicador' : 'Pontos percentuais'}</span><span className={rule.type === 'fixed' ? 'commission-affixed-input' : ''}>{rule.type === 'fixed' && <i>R$</i>}<input type="number" min="0" step={rule.type === 'fixed' ? '10' : '0.1'} value={rule.value} onChange={(event) => onPatch({ value: Number(event.target.value) })} /></span></label>
                </div>
            </div>
            <div className="commission-editor-section">
                <div className="commission-editor-section-title"><span>Aplicação</span><small>Sem seleção, a regra vale para todo o grupo.</small></div>
                <ChoiceGrid legend="Produtos" options={products.map((product) => ({ value: product.slug, label: product.label }))} values={rule.productSlugs ?? []} onChange={(productSlugs) => onPatch({ productSlugs })} allLabel="Todos os produtos" />
                <ChoiceGrid legend="Cargos" options={roleOptions} values={rule.cargoKeys ?? []} onChange={(cargoKeys) => onPatch({ cargoKeys })} allLabel="Todos os cargos" />
                <ChoiceGrid legend="Participação" options={PARTICIPANT_CONTEXTS} values={rule.participantContexts ?? []} onChange={(participantContexts) => onPatch({ participantContexts })} allLabel="Qualquer participação" />
                <ChoiceGrid legend="Forma de pagamento" options={PAYMENT_CATEGORIES} values={rule.paymentCategories ?? []} onChange={(paymentCategories) => onPatch({ paymentCategories })} allLabel="Todas as formas" />
            </div>
            <div className="commission-editor-section">
                <div className="commission-editor-section-title"><span>Condições de entrada</span><small>Use percentual, valor ou ambos. Campos vazios não limitam a regra.</small></div>
                <div className="commission-form-grid commission-form-grid-4">
                    <label className="commission-field"><span>Percentual mínimo</span><span className="commission-suffixed-input"><input type="number" min="0" max="100" step="0.1" value={rule.minCashPercent ?? ''} onChange={(event) => setNullableNumber('minCashPercent', event.target.value)} /><i>%</i></span></label>
                    <label className="commission-field"><span>Percentual máximo</span><span className="commission-suffixed-input"><input type="number" min="0" max="100" step="0.1" value={rule.maxCashPercent ?? ''} onChange={(event) => setNullableNumber('maxCashPercent', event.target.value)} /><i>%</i></span></label>
                    <label className="commission-field"><span>Entrada mínima</span><span className="commission-affixed-input"><i>R$</i><input type="number" min="0" step="100" value={rule.minCashAmount ?? ''} onChange={(event) => setNullableNumber('minCashAmount', event.target.value)} /></span></label>
                    <label className="commission-field"><span>Entrada máxima</span><span className="commission-affixed-input"><i>R$</i><input type="number" min="0" step="100" value={rule.maxCashAmount ?? ''} onChange={(event) => setNullableNumber('maxCashAmount', event.target.value)} /></span></label>
                </div>
            </div>
            <div className="commission-editor-section">
                <div className="commission-editor-section-title"><span>Vigência</span><small>A regra é avaliada pela data da venda.</small></div>
                <div className="commission-form-grid commission-form-grid-3">
                    <label className="commission-field"><span>Começa em</span><input type="date" value={rule.validFrom ?? ''} onChange={(event) => onPatch({ validFrom: event.target.value || null })} /></label>
                    <label className="commission-field"><span>Termina em</span><input type="date" value={rule.validTo ?? ''} onChange={(event) => onPatch({ validTo: event.target.value || null })} /></label>
                    <label className="commission-field"><span>Prioridade</span><input type="number" step="1" value={rule.priority ?? 100} onChange={(event) => onPatch({ priority: Number(event.target.value) || 100 })} /></label>
                </div>
            </div>
            <footer className="commission-editor-footer"><button type="button" className="commission-button commission-button-quiet" onClick={onDuplicate}><Copy size={16} />Duplicar</button><button type="button" className="commission-button commission-button-danger" onClick={onDelete}><Trash2 size={16} />Excluir regra</button></footer>
        </form>
    );
}

function TierGroupEditor({ item, products, roleOptions, onPatchRule, onPatchGroup, onChangeProduct, onAddTier, onDuplicate, onDeleteRule, onDeleteGroup }) {
    if (!item) return null;
    const activeCount = item.rules.filter((rule) => rule.active).length;
    const firstRule = item.rules[0];
    const reference = CASH_COLLECT_PRODUCTS.find((product) => product.slug === item.productSlug);
    return (
        <div className="commission-tier-editor">
            <header className="commission-editor-header"><div className="commission-editor-title"><span className="commission-editor-icon"><Layers3 size={18} /></span><div><span>Faixas progressivas</span><h2>Cash collect · {item.productLabel}</h2></div></div><span className={`commission-status-pill ${activeCount ? 'is-active' : ''}`}><i />{activeCount}/{item.rules.length} ativas</span></header>
            <div className="commission-tier-controls">
                <label className="commission-field"><span>Produto</span><select value={item.productSlug} disabled={!item.rules.every((rule) => String(rule.id).startsWith('draft-'))} onChange={(event) => onChangeProduct(event.target.value)}>{products.map((product) => <option key={product.slug} value={product.slug}>{product.label}</option>)}</select></label>
                <label className="commission-field"><span>Válido a partir de</span><input type="date" value={firstRule?.validFrom ?? ''} onChange={(event) => onPatchGroup({ validFrom: event.target.value || null })} /></label>
                <label className="commission-field"><span>Válido até</span><input type="date" value={firstRule?.validTo ?? ''} onChange={(event) => onPatchGroup({ validTo: event.target.value || null })} /></label>
            </div>
            <div className="commission-editor-section commission-tier-scope">
                <div className="commission-editor-section-title"><span>Quem pode receber</span><small>A maior faixa atingida é aplicada uma única vez por participante.</small></div>
                <ChoiceGrid legend="Cargos" options={roleOptions} values={firstRule?.cargoKeys ?? []} onChange={(cargoKeys) => onPatchGroup({ cargoKeys })} allLabel="Todos os cargos" />
                <ChoiceGrid legend="Participação" options={PARTICIPANT_CONTEXTS} values={firstRule?.participantContexts ?? []} onChange={(participantContexts) => onPatchGroup({ participantContexts })} allLabel="Qualquer participação" />
            </div>
            <div className="commission-editor-section commission-tier-table-section">
                <div className="commission-editor-section-title commission-tier-title-row"><div><span>Faixas</span><small>{reference?.referencePrice ? `Preço de referência atual: ${formatBRL(reference.referencePrice)}` : 'O percentual é calculado sobre o valor total do contrato.'}</small></div><button type="button" className="commission-button commission-button-quiet commission-button-small" onClick={onAddTier}><Plus size={15} />Adicionar faixa</button></div>
                <div className="commission-tier-table">
                    <div className="commission-tier-table-head"><span>Status</span><span>Faixa</span><span>Entrada mínima</span><span>Bônus</span><span aria-hidden="true" /></div>
                    {item.rules.map((rule) => <div className={`commission-tier-row ${rule.active ? '' : 'is-inactive'}`} key={rule.id}>
                        <Toggle checked={rule.active} onChange={(active) => onPatchRule(rule.id, { active })} label={rule.active ? 'Ativa' : 'Inativa'} />
                        <label className="commission-compact-input"><input aria-label={`Percentual da faixa ${rule.name}`} type="number" min="0" max="100" step="1" value={rule.exclusiveRank ?? ''} onChange={(event) => { const rank = Number(event.target.value); onPatchRule(rule.id, { name: `${item.productLabel} · ${rank}% de cash collect`, exclusiveRank: rank, ...(rule.minCashAmount == null ? { minCashPercent: rank } : {}) }); }} /><i>%</i></label>
                        {rule.minCashAmount != null ? <label className="commission-compact-input is-money"><i>R$</i><input aria-label={`Entrada mínima ${rule.name}`} type="number" min="0" step="100" value={rule.minCashAmount} onChange={(event) => onPatchRule(rule.id, { minCashAmount: Number(event.target.value) })} /></label> : <span className="commission-derived-value">{Number(rule.minCashPercent ?? rule.exclusiveRank ?? 0).toLocaleString('pt-BR')}% do contrato</span>}
                        <label className="commission-compact-input is-money"><i>R$</i><input aria-label={`Bônus ${rule.name}`} type="number" min="0" step="10" value={rule.value} onChange={(event) => onPatchRule(rule.id, { value: Number(event.target.value) })} /></label>
                        <button type="button" className="commission-icon-button" aria-label={`Excluir ${rule.name}`} onClick={() => onDeleteRule(rule)}><Trash2 size={15} /></button>
                    </div>)}
                </div>
            </div>
            <footer className="commission-editor-footer"><button type="button" className="commission-button commission-button-quiet" onClick={onDuplicate}><Copy size={16} />Duplicar conjunto</button><button type="button" className="commission-button commission-button-danger" onClick={onDeleteGroup}><Trash2 size={16} />Excluir conjunto</button></footer>
        </div>
    );
}

export default function Comissionamento({ previewData = null }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedView = searchParams.get('visao') || 'rules';
    const view = VIEW_ALIASES[requestedView] || 'rules';
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [roleDrafts, setRoleDrafts] = useState([]);
    const [products, setProducts] = useState([]);
    const [rulesConfig, setRulesConfig] = useState({ rules: [] });
    const [pointWeights, setPointWeights] = useState({ closer: {} });
    const [rulesDirty, setRulesDirty] = useState(false);
    const [productsDirty, setProductsDirty] = useState(false);
    const [rolesDirty, setRolesDirty] = useState(false);
    const [dirtyRoleIds, setDirtyRoleIds] = useState(() => new Set());
    const [simulation, setSimulation] = useState(EMPTY_SIMULATION);
    const [selectedItemKey, setSelectedItemKey] = useState('');
    const [ruleSearch, setRuleSearch] = useState('');
    const [ruleProductFilter, setRuleProductFilter] = useState('all');
    const [ruleStatusFilter, setRuleStatusFilter] = useState('all');
    const [dataPanelOpen, setDataPanelOpen] = useState(false);
    // O contador de pendências dizia quantas, nunca quais. A única forma de ler alguma era abrir o
    // Simulador (que mostra só a primeira) ou tentar publicar. Agora a pílula abre a lista inteira.
    const [pendingPanelOpen, setPendingPanelOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [deleteRequest, setDeleteRequest] = useState(null);
    const reviewReturnFocusRef = useRef(null);
    const reviewCloseRef = useRef(null);
    const dataTriggerRef = useRef(null);
    const dataCloseRef = useRef(null);
    const savingRef = useRef(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [cargos, productSetting, config, weights] = previewData
                ? [previewData.cargos, previewData.products, previewData.rulesConfig, previewData.pointWeights]
                : await Promise.all([
                    getCargos({ apenasAtivos: true }),
                    getAppSetting('produtos_config'),
                    getCommissionRulesConfig(),
                    getAppSetting('gamification_weights_rankings'),
                ]);
            const productList = requireConfiguredProducts(productSetting);
            setRoleDrafts((cargos ?? []).map(buildRoleDraft));
            setProducts(productList);
            setRulesConfig(config ?? { rules: [] });
            setPointWeights(toNewConfig(weights));
            setSimulation((current) => ({
                ...current,
                sellerCargoId: current.sellerCargoId || cargos?.find((role) => ['closer', 'gestor_comercial'].includes(commissionRuleCargoKey(role.nome)))?.id || '',
                productSlug: current.productSlug || productList[0]?.slug || '',
            }));
            setRolesDirty(false);
            setDirtyRoleIds(new Set());
            setProductsDirty(false);
            setRulesDirty(false);
            return true;
        } catch (requestError) {
            setError(requestError?.message || 'Não foi possível carregar o plano de comissionamento.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [previewData]);

    useEffect(() => { load(); }, [load]);

    const hasUnsavedChanges = rulesDirty || productsDirty || rolesDirty;
    useEffect(() => { savingRef.current = saving; }, [saving]);

    useEffect(() => {
        if (!reviewOpen) return undefined;
        reviewCloseRef.current?.focus();
        function closeOnEscape(event) {
            if (event.key === 'Escape' && !savingRef.current) setReviewOpen(false);
        }
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('keydown', closeOnEscape);
            window.requestAnimationFrame(() => reviewReturnFocusRef.current?.focus());
        };
    }, [reviewOpen]);

    useEffect(() => {
        if (!dataPanelOpen) return undefined;
        const returnFocus = dataTriggerRef.current;
        dataCloseRef.current?.focus();
        function closeDataOnEscape(event) {
            if (event.key === 'Escape') setDataPanelOpen(false);
        }
        document.addEventListener('keydown', closeDataOnEscape);
        return () => {
            document.removeEventListener('keydown', closeDataOnEscape);
            window.requestAnimationFrame(() => returnFocus?.focus());
        };
    }, [dataPanelOpen]);

    useEffect(() => {
        if (!deleteRequest) return undefined;
        function closeDeleteOnEscape(event) {
            if (event.key === 'Escape') setDeleteRequest(null);
        }
        document.addEventListener('keydown', closeDeleteOnEscape);
        return () => document.removeEventListener('keydown', closeDeleteOnEscape);
    }, [deleteRequest]);

    useEffect(() => {
        function warnBeforeLeaving(event) {
            if (!hasUnsavedChanges) return;
            event.preventDefault();
            event.returnValue = '';
        }
        window.addEventListener('beforeunload', warnBeforeLeaving);
        return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
    }, [hasUnsavedChanges]);

    // Todo cargo que o motor paga em venda: closer, SDR (pré-venda), Social Seller (número
    // coletado) e gestor. Faltava o Social Seller, e por isso a taxa dele só mudava no banco.
    const operationalRoles = useMemo(() => roleDrafts
        .filter((role) => ['closer', 'sdr', 'social_seller', 'gestor_comercial'].includes(commissionRuleCargoKey(role.nome)))
        .sort((a, b) => {
            const order = { closer: 1, sdr: 2, social_seller: 3, gestor_comercial: 4 };
            return order[commissionRuleCargoKey(a.nome)] - order[commissionRuleCargoKey(b.nome)] || Number(a.nivel ?? 1) - Number(b.nivel ?? 1);
        }), [roleDrafts]);
    const sellerRoles = useMemo(() => operationalRoles.filter((role) => ['closer', 'gestor_comercial'].includes(commissionRuleCargoKey(role.nome))), [operationalRoles]);
    const preSaleRoles = useMemo(() => operationalRoles.filter((role) => commissionRuleCargoKey(role.nome) === 'sdr'), [operationalRoles]);
    const managerRole = useMemo(() => operationalRoles.find((role) => commissionRuleCargoKey(role.nome) === 'gestor_comercial'), [operationalRoles]);
    const simulationSellerRole = useMemo(() => sellerRoles.find((role) => role.id === simulation.sellerCargoId), [sellerRoles, simulation.sellerCargoId]);
    const simulationForcesManagement = commissionRuleCargoKey(simulationSellerRole?.nome) === 'gestor_comercial';
    const productRoleOptions = useMemo(() => {
        const map = new Map();
        for (const role of operationalRoles) map.set(productRateKey(role), roleLabel(role));
        return Array.from(map, ([value, label]) => ({ value, label }));
    }, [operationalRoles]);
    const ruleRoleOptions = useMemo(() => {
        const map = new Map();
        for (const role of operationalRoles) map.set(commissionRuleCargoKey(role.nome), roleLabel(role));
        return Array.from(map, ([value, label]) => ({ value, label }));
    }, [operationalRoles]);
    const roleLabels = useMemo(() => new Map(ruleRoleOptions.map((role) => [role.value, role.label])), [ruleRoleOptions]);
    const productLabels = useMemo(() => new Map(products.map((product) => [product.slug, product.label])), [products]);
    const inventory = useMemo(() => buildRuleInventory(rulesConfig.rules ?? [], productLabels), [productLabels, rulesConfig.rules]);
    const filteredInventory = useMemo(() => inventory.filter((item) => {
        const rules = item.kind === 'tier-group' ? item.rules : [item.rule];
        const haystack = item.kind === 'tier-group' ? `cash collect ${item.productLabel}` : `${item.rule.name} ${ruleConditionLabel(item.rule, roleLabels, productLabels)}`;
        if (ruleSearch && !haystack.toLowerCase().includes(ruleSearch.trim().toLowerCase())) return false;
        if (ruleProductFilter !== 'all' && !rules.some((rule) => !rule.productSlugs?.length || rule.productSlugs.includes(ruleProductFilter))) return false;
        if (ruleStatusFilter === 'active' && !rules.some((rule) => rule.active)) return false;
        if (ruleStatusFilter === 'inactive' && rules.some((rule) => rule.active)) return false;
        return true;
    }), [inventory, productLabels, roleLabels, ruleProductFilter, ruleSearch, ruleStatusFilter]);

    useEffect(() => {
        if (selectedItemKey && inventory.some((item) => item.key === selectedItemKey)) return;
        setSelectedItemKey(filteredInventory[0]?.key || inventory[0]?.key || '');
    }, [filteredInventory, inventory, selectedItemKey]);

    const selectedItem = inventory.find((item) => item.key === selectedItemKey) ?? null;
    const activeRules = rulesConfig.rules.filter((rule) => rule.active);
    const coversAllProducts = activeRules.some((rule) => !rule.productSlugs?.length);
    const coveredProducts = new Set(activeRules.flatMap((rule) => rule.productSlugs ?? [])).size;
    const productInvariants = useMemo(() => verificarInvariantesCloser(products, pointWeights?.closer), [pointWeights, products]);
    const draftValidationErrors = useMemo(() => validatePlanDraft({ roleDrafts, products, rules: rulesConfig.rules, productInvariants }), [productInvariants, products, roleDrafts, rulesConfig.rules]);
    const cashProducts = useMemo(() => CASH_COLLECT_PRODUCTS.map((product) => ({
        ...product,
        rules: rulesConfig.rules.filter((rule) => isCashCollectRule(rule) && rule.productSlugs?.includes(product.slug)),
    })), [rulesConfig.rules]);

    const simulationResult = useMemo(() => {
        const sellerRole = simulationSellerRole;
        const preSaleRole = preSaleRoles.find((role) => role.id === simulation.preSaleCargoId);
        const product = products.find((item) => item.slug === simulation.productSlug);
        const contractTotal = Math.max(0, Number(simulation.contractTotal) || 0);
        const commissionBase = Math.max(0, Number(simulation.commissionBase) || 0);
        const firstMonthCash = Math.max(0, Number(simulation.firstMonthCash) || 0);
        if (draftValidationErrors.length || !sellerRole || !product || contractTotal <= 0 || commissionBase <= 0 || !isValidIsoDate(simulation.eventDate)) return null;
        const context = { commissionBase, productSlug: product.slug, paymentCategory: simulation.paymentCategory, contractTotal, firstMonthCash, eventDate: simulation.eventDate };
        const seller = calculateCommissionScenario({ ...context, baseRate: rateForParticipant(sellerRole, product, 'seller'), participantContext: 'seller', cargo: sellerRole.nome }, rulesConfig);
        const preSale = preSaleRole ? calculateCommissionScenario({ ...context, baseRate: rateForParticipant(preSaleRole, product, 'pre_sale'), participantContext: 'pre_sale', cargo: preSaleRole.nome }, rulesConfig) : null;
        const includeManagement = simulationForcesManagement || simulation.includeManagement;
        const management = includeManagement && managerRole ? calculateCommissionScenario({ ...context, baseRate: rateForParticipant(managerRole, product, 'manager'), participantContext: 'manager', cargo: managerRole.nome }, rulesConfig) : null;
        const productCashPlan = cashProducts.find((item) => item.slug === product.slug);
        const tierCandidates = (productCashPlan?.rules ?? []).filter((rule) => rule.active).map((rule) => ({ rule, threshold: ruleThreshold(rule, productCashPlan?.referencePrice, contractTotal) })).sort((a, b) => a.threshold - b.threshold);
        const reachedTier = [...tierCandidates].reverse().find((tier) => firstMonthCash >= tier.threshold) ?? null;
        const nextTier = tierCandidates.find((tier) => firstMonthCash < tier.threshold) ?? null;
        return {
            sellerRole, preSaleRole, product, seller, preSale, management,
            totalCommission: seller.totalCommission + (preSale?.totalCommission ?? 0) + (management?.totalCommission ?? 0),
            contractTotal, commissionBase, firstMonthCash,
            cashPercent: contractTotal > 0 ? firstMonthCash / contractTotal * 100 : 0,
            reachedTier, nextTier,
        };
    }, [cashProducts, draftValidationErrors.length, managerRole, preSaleRoles, products, rulesConfig, simulation, simulationForcesManagement, simulationSellerRole]);

    function changeView(nextView) {
        const next = new URLSearchParams(searchParams);
        if (nextView === 'rules') next.delete('visao');
        else next.set('visao', nextView);
        setSearchParams(next, { replace: true });
    }

    function setSuccess(message) {
        setNotice(message);
        setError('');
        window.setTimeout(() => setNotice(''), 4000);
    }

    function patchRule(ruleId, patch) {
        setRulesConfig((current) => ({ ...current, rules: current.rules.map((rule) => rule.id === ruleId ? { ...rule, ...patch } : rule) }));
        setRulesDirty(true);
    }

    function patchGroup(groupName, patch) {
        setRulesConfig((current) => ({ ...current, rules: current.rules.map((rule) => rule.exclusiveGroup === groupName ? { ...rule, ...patch } : rule) }));
        setRulesDirty(true);
    }

    function beginNewRule(mode) {
        const rule = createRule({ mode, productSlug: products[0]?.slug || '' });
        setRulesConfig((current) => ({ ...current, rules: [...current.rules, rule] }));
        setRulesDirty(true);
        setSelectedItemKey(mode === 'tier' ? `group:${rule.exclusiveGroup}` : `rule:${rule.id}`);
    }

    function duplicateRule(rule) {
        const id = globalThis.crypto?.randomUUID?.() || `rule-${Date.now()}`;
        const copy = { ...rule, id: `draft-${id}`, name: `${rule.name} · cópia`, active: false };
        setRulesConfig((current) => ({ ...current, rules: [...current.rules, copy] }));
        setRulesDirty(true);
        setSelectedItemKey(`rule:${copy.id}`);
    }

    function duplicateGroup(item) {
        const suffix = globalThis.crypto?.randomUUID?.() || Date.now();
        const groupName = `cash-collect:${item.productSlug}:copy-${suffix}`;
        const copies = item.rules.map((rule, index) => ({ ...rule, id: `draft-${suffix}-${index}`, name: `${rule.name} · cópia`, active: false, exclusiveGroup: groupName }));
        setRulesConfig((current) => ({ ...current, rules: [...current.rules, ...copies] }));
        setRulesDirty(true);
        setSelectedItemKey(`group:${groupName}`);
    }

    function addTier(item) {
        const last = item.rules[item.rules.length - 1];
        const rank = Number(last?.exclusiveRank ?? 0) + 10;
        const id = globalThis.crypto?.randomUUID?.() || `tier-${Date.now()}`;
        const rule = {
            ...last,
            id: `draft-${id}`,
            name: `${item.productLabel} · ${rank}% de cash collect`,
            active: false,
            value: 0,
            exclusiveRank: rank,
            minCashPercent: last?.minCashAmount == null ? rank : null,
            minCashAmount: last?.minCashAmount == null ? null : 0,
        };
        setRulesConfig((current) => ({ ...current, rules: [...current.rules, rule] }));
        setRulesDirty(true);
    }

    function changeGroupProduct(item, productSlug) {
        const suffix = item.groupName.split(':').slice(2).join(':');
        const groupName = `cash-collect:${productSlug}${suffix ? `:${suffix}` : ''}`;
        const label = productLabels.get(productSlug) ?? productSlug;
        setRulesConfig((current) => ({ ...current, rules: current.rules.map((rule) => rule.exclusiveGroup === item.groupName ? { ...rule, name: `${label} · ${rule.exclusiveRank ?? 0}% de cash collect`, productSlugs: [productSlug], exclusiveGroup: groupName } : rule) }));
        setRulesDirty(true);
        setSelectedItemKey(`group:${groupName}`);
    }

    function updateRole(roleId, field, value) {
        setRoleDrafts((current) => current.map((role) => role.id === roleId ? { ...role, display: { ...role.display, [field]: value } } : role));
        setRolesDirty(true);
        setDirtyRoleIds((current) => new Set(current).add(roleId));
    }

    function updateProductRate(slug, cargoKey, value) {
        setProducts((current) => current.map((product) => {
            if (product.slug !== slug) return product;
            const commissions = { ...(product.comissoes ?? {}) };
            if (value === '') delete commissions[cargoKey];
            else commissions[cargoKey] = Number(value) / 100;
            return { ...product, comissoes: Object.keys(commissions).length ? commissions : undefined };
        }));
        setProductsDirty(true);
    }

    function applyOfficialRoleRates() {
        const applicable = roleDrafts.filter((role) => officialSaleRateForCargo(role.nome, role.nivel) != null || officialManagerOverrideRateForCargo(role.nome, role.nivel) != null);
        if (!applicable.length) {
            setError('Nenhum cargo compatível foi encontrado.');
            return;
        }
        setRoleDrafts((current) => current.map((role) => {
            const saleRate = officialSaleRateForCargo(role.nome, role.nivel);
            const managerRate = officialManagerOverrideRateForCargo(role.nome, role.nivel);
            if (saleRate == null && managerRate == null) return role;
            return { ...role, display: { ...role.display, ...(saleRate == null ? {} : { comissao_venda: displayRate('percent', saleRate) }), ...(managerRate == null ? {} : { comissao_venda_override: displayRate('percent', managerRate) }) } };
        }));
        setRolesDirty(true);
        setDirtyRoleIds(new Set(applicable.map((role) => role.id)));
        setSuccess('Percentuais oficiais aplicados ao rascunho.');
    }

    function applyOfficialCashCollectPreset() {
        const officialRules = buildOfficialCashCollectRules({ cargoKeys: OFFICIAL_CASH_COLLECT_CARGO_KEYS, validFrom: OFFICIAL_COMMISSION_VALID_FROM });
        setRulesConfig((current) => ({ ...current, rules: [...current.rules.filter((rule) => !String(rule.exclusiveGroup ?? '').match(/^cash-collect:(performance|expansao|tracao)$/)), ...officialRules] }));
        setRulesDirty(true);
        setSelectedItemKey('group:cash-collect:performance');
        setSuccess('Modelo confirmado de cash collect aplicado ao rascunho.');
    }

    function requestDelete(ids, label) {
        setDeleteRequest({ ids, label });
    }

    function confirmDelete() {
        const ids = new Set(deleteRequest?.ids ?? []);
        setRulesConfig((current) => ({ ...current, rules: current.rules.filter((rule) => !ids.has(rule.id)) }));
        setRulesDirty(true);
        setDeleteRequest(null);
        setSelectedItemKey('');
    }

    function openPublishReview(event) {
        if (draftValidationErrors.length) {
            setNotice('');
            setError(`Corrija ${draftValidationErrors.length} pendência(s) antes de publicar. ${draftValidationErrors.slice(0, 2).join(' ')}`);
            return;
        }
        reviewReturnFocusRef.current = event.currentTarget;
        setError('');
        setReviewOpen(true);
    }

    function trapReviewFocus(event) {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(event.currentTarget.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    async function publishPlan() {
        const validationErrors = validatePlanDraft({ roleDrafts, products, rules: rulesConfig.rules, productInvariants });
        if (validationErrors.length) {
            setReviewOpen(false);
            setError(`A publicação foi bloqueada sem gravar dados. ${validationErrors.slice(0, 3).join(' ')}`);
            return;
        }
        if (previewData) {
            setReviewOpen(false);
            setRulesDirty(false);
            setProductsDirty(false);
            setRolesDirty(false);
            setDirtyRoleIds(new Set());
            setSuccess('Prévia validada. Nenhum dado real foi gravado.');
            return;
        }
        const rolesToPublish = roleDrafts.filter((role) => dirtyRoleIds.has(role.id));
        const publication = { roles: [], products: false, rules: false };
        let currentStage = 'inicialização';
        setSaving(true);
        setError('');
        try {
            if (rolesDirty) {
                for (const role of rolesToPublish) {
                    currentStage = `${roleLabel(role)} N${role.nivel ?? 1}`;
                    const payload = { ...role, acao: 'editar' };
                    for (const field of RATE_FIELDS) {
                        payload[`${field.key}_tipo`] = field.type;
                        payload[`${field.key}_valor`] = persistedRate(field.type, role.display[field.key]);
                    }
                    payload.comissao_venda_override_valor = role.display.comissao_venda_override === '' ? null : (Number(role.display.comissao_venda_override) || 0) / 100;
                    delete payload.display;
                    await salvarCargo(payload);
                    publication.roles.push(currentStage);
                }
                setRolesDirty(false);
                setDirtyRoleIds(new Set());
            }
            if (productsDirty) {
                currentStage = 'exceções por produto';
                await setAppSetting('produtos_config', products);
                invalidateProdutosCache();
                publication.products = true;
                setProductsDirty(false);
            }
            if (rulesDirty) {
                currentStage = 'regras e faixas';
                const saved = await saveCommissionRulesConfig(rulesConfig);
                setRulesConfig(saved);
                publication.rules = true;
                setRulesDirty(false);
            }
            setReviewOpen(false);
            const reloaded = await load();
            if (reloaded) setSuccess('Plano publicado. Novos lançamentos já usam a configuração.');
        } catch (saveError) {
            setReviewOpen(false);
            const persisted = [];
            if (publication.roles.length) persisted.push(`${publication.roles.length} cargo(s)`);
            if (publication.products) persisted.push('exceções por produto');
            if (publication.rules) persisted.push('regras');
            setError(`Falha ao publicar ${currentStage}: ${saveError?.message || 'erro não identificado.'}${persisted.length ? ` Já persistido: ${persisted.join(', ')}.` : ' Nenhuma etapa foi confirmada.'}`);
        } finally {
            setSaving(false);
        }
    }

    if (loading && !roleDrafts.length) return <div className="commission-loading"><Loader2 size={20} className="commission-spin" />Carregando plano de comissionamento</div>;

    return (
        <main className="commission-page">
            <header className="commission-header">
                <div className="commission-header-copy">
                    <div className="commission-title-row"><h1>Comissionamento</h1>
                        {draftValidationErrors.length ? (
                            <div className="commission-pending-wrap">
                                <button type="button" className="commission-status-pill is-warning is-clickable" aria-expanded={pendingPanelOpen} onClick={() => setPendingPanelOpen((open) => !open)}><i />{draftValidationErrors.length} pendência(s)<ChevronDown size={13} className={pendingPanelOpen ? 'commission-pending-caret is-open' : 'commission-pending-caret'} /></button>
                                {pendingPanelOpen && <div className="commission-pending-panel" role="group" aria-label="Pendências que bloqueiam a publicação">
                                    <strong>Bloqueiam a publicação</strong>
                                    <ul>{draftValidationErrors.map((message, index) => <li key={`${index}-${message}`}>{message}</li>)}</ul>
                                </div>}
                            </div>
                        ) : <span className="commission-status-pill is-active"><i />Plano válido</span>}
                    </div>
                    <p>Regras comerciais usadas em novos lançamentos do Desk.</p>
                </div>
                <div className="commission-header-actions">
                    <button ref={dataTriggerRef} type="button" className="commission-button commission-button-quiet" onClick={() => setDataPanelOpen(true)}><Database size={16} />Dados do cálculo</button>
                    {/* O CSS já apaga o botão desabilitado (opacity .45 + cursor not-allowed); o que
                        faltava era dizer POR QUE ele não aceita clique. */}
                    <button type="button" className="commission-button" onClick={openPublishReview} disabled={!hasUnsavedChanges || saving} title={saving ? 'Publicando…' : hasUnsavedChanges ? 'Revisar as mudanças antes de publicar' : 'Nada mudou desde a última publicação'}><Save size={16} />Revisar e publicar</button>
                </div>
            </header>

            <div className="commission-commandbar">
                <nav className="commission-tabs" aria-label="Áreas do comissionamento">
                    <button type="button" className={view === 'rules' ? 'active' : ''} aria-current={view === 'rules' ? 'page' : undefined} onClick={() => changeView('rules')}><CircleDollarSign size={16} />Regras e bônus</button>
                    <button type="button" className={view === 'rates' ? 'active' : ''} aria-current={view === 'rates' ? 'page' : undefined} onClick={() => changeView('rates')}><Percent size={16} />Percentuais</button>
                    <button type="button" className={view === 'simulator' ? 'active' : ''} aria-current={view === 'simulator' ? 'page' : undefined} onClick={() => changeView('simulator')}><Calculator size={16} />Simulador</button>
                </nav>
                <div className="commission-save-state">{hasUnsavedChanges ? <><i />Alterações não publicadas</> : <>Atualizado</>}</div>
            </div>

            {error && <div className="commission-alert commission-alert-error" role="alert"><AlertTriangle size={17} /><span>{error}</span><button type="button" aria-label="Fechar aviso" onClick={() => setError('')}><X size={15} /></button></div>}
            {notice && <div className="commission-alert commission-alert-success" role="status"><Check size={17} />{notice}</div>}

            {view === 'rules' && (
                <section className="commission-rules-view">
                    <div className="commission-metric-strip">
                        <div><span>Regras ativas</span><strong>{activeRules.length}</strong><small>de {rulesConfig.rules.length} cadastradas</small></div>
                        <div><span>Produtos cobertos</span><strong>{coversAllProducts ? 'Todos' : coveredProducts}</strong><small>{coversAllProducts ? `${products.length} no catálogo` : `de ${products.length} no catálogo`}</small></div>
                        <div><span>Faixas progressivas</span><strong>{inventory.filter((item) => item.kind === 'tier-group').length}</strong><small>paga apenas a maior atingida</small></div>
                        <button type="button" onClick={() => changeView('simulator')}><TestTube2 size={17} /><span><strong>Testar o plano</strong><small>Simular sem gravar</small></span></button>
                    </div>

                    <div className="commission-rules-workspace">
                        <aside className="commission-rule-sidebar">
                            <header>
                                <div><span>Configurações</span><strong>Bonificações</strong></div>
                                <details className="commission-create-menu">
                                    <summary><Plus size={16} />Nova<ChevronDown size={14} /></summary>
                                    <div>
                                        <button type="button" onClick={(event) => { beginNewRule('single'); event.currentTarget.closest('details')?.removeAttribute('open'); }}><SlidersHorizontal size={17} /><span><strong>Regra única</strong><small>Pagamento, cargo, produto ou condição específica</small></span></button>
                                        <button type="button" onClick={(event) => { beginNewRule('tier'); event.currentTarget.closest('details')?.removeAttribute('open'); }}><Layers3 size={17} /><span><strong>Faixas progressivas</strong><small>Somente a maior faixa atingida é paga</small></span></button>
                                    </div>
                                </details>
                            </header>
                            <div className="commission-rule-tools">
                                <label className="commission-search"><Search size={15} /><input aria-label="Buscar configurações" type="search" placeholder="Buscar regra ou produto" value={ruleSearch} onChange={(event) => setRuleSearch(event.target.value)} /></label>
                                <div>
                                    <select aria-label="Filtrar por produto" value={ruleProductFilter} onChange={(event) => setRuleProductFilter(event.target.value)}><option value="all">Todos os produtos</option>{products.map((product) => <option key={product.slug} value={product.slug}>{product.label}</option>)}</select>
                                    <select aria-label="Filtrar por status" value={ruleStatusFilter} onChange={(event) => setRuleStatusFilter(event.target.value)}><option value="all">Todos os status</option><option value="active">Ativas</option><option value="inactive">Inativas</option></select>
                                </div>
                            </div>
                            <div className="commission-rule-list" role="listbox" aria-label="Regras e conjuntos de bonificação">
                                {filteredInventory.map((item) => {
                                    const rules = item.kind === 'tier-group' ? item.rules : [item.rule];
                                    const isActive = rules.some((rule) => rule.active);
                                    const label = item.kind === 'tier-group' ? `Cash collect · ${item.productLabel}` : item.rule.name;
                                    const result = item.kind === 'tier-group' ? `${item.rules.length} faixas · até ${formatBRL(Math.max(...item.rules.map((rule) => Number(rule.value) || 0)))}` : ruleResultLabel(item.rule);
                                    const condition = item.kind === 'tier-group' ? 'Maior faixa atingida' : ruleConditionLabel(item.rule, roleLabels, productLabels);
                                    return (
                                        <button type="button" role="option" aria-selected={selectedItemKey === item.key} className={`commission-rule-list-item ${selectedItemKey === item.key ? 'selected' : ''}`} key={item.key} onClick={() => setSelectedItemKey(item.key)}>
                                            <span className={`commission-rule-type-icon ${item.kind === 'tier-group' ? 'is-tier' : ''}`}>{item.kind === 'tier-group' ? <Layers3 size={16} /> : <SlidersHorizontal size={16} />}</span>
                                            <span className="commission-rule-list-copy"><span><strong>{label}</strong><i className={isActive ? 'is-active' : ''}>{isActive ? 'Ativa' : 'Inativa'}</i></span><small>{condition}</small><b>{result}</b></span>
                                        </button>
                                    );
                                })}
                                {!filteredInventory.length && <div className="commission-empty-list"><Search size={19} /><strong>Nenhuma configuração encontrada</strong><span>Limpe os filtros ou crie uma nova bonificação.</span></div>}
                            </div>
                            <footer><button type="button" onClick={applyOfficialCashCollectPreset}><RefreshCw size={15} />Aplicar modelo confirmado de cash collect</button></footer>
                        </aside>

                        <section className="commission-rule-detail">
                            {selectedItem?.kind === 'rule' && <RuleEditor rule={selectedItem.rule} products={products} roleOptions={ruleRoleOptions} onPatch={(patch) => patchRule(selectedItem.rule.id, patch)} onDuplicate={() => duplicateRule(selectedItem.rule)} onDelete={() => requestDelete([selectedItem.rule.id], selectedItem.rule.name)} />}
                            {selectedItem?.kind === 'tier-group' && <TierGroupEditor item={selectedItem} products={products} roleOptions={ruleRoleOptions} onPatchRule={patchRule} onPatchGroup={(patch) => patchGroup(selectedItem.groupName, patch)} onChangeProduct={(productSlug) => changeGroupProduct(selectedItem, productSlug)} onAddTier={() => addTier(selectedItem)} onDuplicate={() => duplicateGroup(selectedItem)} onDeleteRule={(rule) => requestDelete([rule.id], rule.name)} onDeleteGroup={() => requestDelete(selectedItem.rules.map((rule) => rule.id), `Cash collect · ${selectedItem.productLabel}`)} />}
                            {!selectedItem && <div className="commission-empty-detail"><Layers3 size={24} /><strong>Crie a primeira bonificação</strong><span>Escolha uma regra única ou um conjunto de faixas progressivas.</span><button type="button" className="commission-button" onClick={() => beginNewRule('single')}><Plus size={16} />Criar regra</button></div>}
                        </section>
                    </div>
                </section>
            )}

            {view === 'rates' && (
                <section className="commission-rates-view">
                    <header className="commission-section-header"><div><span>Base por cargo</span><h2>Percentuais de venda e gestão</h2><p>Valores usados quando o produto não possui uma exceção específica.</p></div><button type="button" className="commission-button commission-button-quiet" onClick={applyOfficialRoleRates}><Sparkles size={16} />Aplicar percentuais confirmados</button></header>
                    <div className="commission-role-table">
                        <div className="commission-role-head"><span>Cargo e nível</span><span>Venda própria</span><span>Gestão do time</span><span>Aplicação</span></div>
                        {operationalRoles.map((role) => {
                            const isManager = commissionRuleCargoKey(role.nome) === 'gestor_comercial';
                            return <div className="commission-role-row" key={role.id}><span className="commission-role-name"><i>{roleLabel(role).slice(0, 1)}</i><span><strong>{roleLabel(role)}</strong><small>Nível {role.nivel ?? 1}</small></span></span><label className="commission-rate-input"><input aria-label={`Venda própria de ${roleLabel(role)} nível ${role.nivel ?? 1}`} type="number" min="0" max="100" step="0.1" value={role.display.comissao_venda} onChange={(event) => updateRole(role.id, 'comissao_venda', event.target.value)} /><i>%</i></label>{isManager ? <label className="commission-rate-input"><input aria-label="Gestão do time" type="number" min="0" max="100" step="0.1" value={role.display.comissao_venda_override} onChange={(event) => updateRole(role.id, 'comissao_venda_override', event.target.value)} /><i>%</i></label> : <span className="commission-not-applicable">—</span>}<span className="commission-rate-note">{roleRateNote(role)}</span></div>;
                        })}
                    </div>
                    <div className="commission-product-rates">
                        <header><div><span>Exceções por produto</span><h2>Substituições da taxa-base</h2><p>Preencha somente quando um produto pagar percentual diferente do cargo.</p></div><span>{products.reduce((total, product) => total + Object.keys(product.comissoes ?? {}).length, 0)} configuradas</span></header>
                        {productInvariants.bloqueios.length > 0 && <div className="commission-catalog-warning is-blocker"><AlertTriangle size={15} /><span><strong>Publicação bloqueada pelo catálogo compartilhado</strong><small>{productInvariants.bloqueios[0].resumo} Corrija a tabela de pesos em Metas & Pontuação.</small></span></div>}
                        {productInvariants.avisos.length > 0 && <div className="commission-catalog-warning"><AlertTriangle size={15} /><span><strong>{productInvariants.avisos.length} aviso(s) no catálogo compartilhado</strong><small>{productInvariants.avisos[0].resumo}</small></span></div>}
                        <div className="commission-product-rate-table">
                            <div className="commission-product-rate-head"><span>Produto</span>{productRoleOptions.map((role) => <span key={role.value}>{role.label}</span>)}</div>
                            {products.map((product) => <div className="commission-product-rate-row" key={product.slug}><span><Package size={15} /><strong>{product.label}</strong></span>{productRoleOptions.map((role) => role.value === MANAGER_PRODUCT_KEY ? (
                                // Somente leitura, de propósito. Esta chave PAGA hoje: os 2% de gestão da venda
                                // de PCL de 27/06 (R$ 79,94 sobre R$ 3.997) saíram daqui. Um botão que a apagasse
                                // estaria a dois cliques de cortar comissão sem rastro — o valor fica visível e
                                // congelado até ser migrado para duas chaves (venda própria × participação de
                                // gestão), como bd_cargos já separa.
                                product.comissoes?.[MANAGER_PRODUCT_KEY] == null
                                    ? <span className="commission-not-applicable" key={role.value}>—</span>
                                    : <span className="commission-legacy-rate" key={role.value} title={`Participação de gestão de ${product.label}, herdada da configuração atual. Será migrada para uma chave própria.`}>{Number(product.comissoes[MANAGER_PRODUCT_KEY]) * 100}% <em>a migrar</em></span>
                            ) : <label className="commission-rate-input" key={role.value}><input aria-label={`${product.label}, ${role.label}`} type="number" min="0" max="100" step="0.1" placeholder="Padrão" value={product.comissoes?.[role.value] == null ? '' : Number(product.comissoes[role.value]) * 100} onChange={(event) => updateProductRate(product.slug, role.value, event.target.value)} /><i>%</i></label>)}</div>)}
                        </div>
                    </div>
                </section>
            )}

            {view === 'simulator' && (
                <section className="commission-simulator-view">
                    <header className="commission-section-header"><div><span>Teste controlado</span><h2>Simular uma venda</h2><p>Calcula com o rascunho atual sem criar lançamento no banco.</p></div><span className="commission-status-pill"><TestTube2 size={14} />Não grava dados</span></header>
                    <div className="commission-simulator-layout">
                        <form className="commission-simulation-form" onSubmit={(event) => event.preventDefault()}>
                            <div className="commission-form-section">
                                <h3>Participantes</h3>
                                <div className="commission-form-grid commission-form-grid-2">
                                    <label className="commission-field"><span>Quem vendeu</span><select value={simulation.sellerCargoId} onChange={(event) => setSimulation({ ...simulation, sellerCargoId: event.target.value })}><option value="">Selecione…</option>{sellerRoles.map((role) => <option key={role.id} value={role.id}>{roleLabel(role)} · N{role.nivel ?? 1}</option>)}</select></label>
                                    <label className="commission-field"><span>Pré-venda / SDR</span><select value={simulation.preSaleCargoId} onChange={(event) => setSimulation({ ...simulation, preSaleCargoId: event.target.value })}><option value="">Sem participação</option>{preSaleRoles.map((role) => <option key={role.id} value={role.id}>{roleLabel(role)} · N{role.nivel ?? 1}</option>)}</select></label>
                                </div>
                                <Toggle checked={simulationForcesManagement || simulation.includeManagement} disabled={simulationForcesManagement} onChange={(includeManagement) => setSimulation({ ...simulation, includeManagement })} label="Incluir participação de gestão" />
                            </div>
                            <div className="commission-form-section">
                                <h3>Venda</h3>
                                <div className="commission-form-grid commission-form-grid-2">
                                    <label className="commission-field"><span>Produto</span><select value={simulation.productSlug} onChange={(event) => setSimulation({ ...simulation, productSlug: event.target.value })}><option value="">Selecione…</option>{products.map((product) => <option key={product.slug} value={product.slug}>{product.label}</option>)}</select></label>
                                    <label className="commission-field"><span>Forma de pagamento</span><select value={simulation.paymentCategory} onChange={(event) => setSimulation({ ...simulation, paymentCategory: event.target.value })}>{PAYMENT_CATEGORIES.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}</select></label>
                                </div>
                            </div>
                            <div className="commission-form-section">
                                <h3>Valores</h3>
                                <div className="commission-form-grid commission-form-grid-3">
                                    <label className="commission-field"><span>Contrato</span><span className="commission-affixed-input"><i>R$</i><input type="number" min="0" step="100" placeholder="0,00" value={simulation.contractTotal} onChange={(event) => setSimulation({ ...simulation, contractTotal: event.target.value })} /></span></label>
                                    <label className="commission-field"><span>Entrada no 1º mês</span><span className="commission-affixed-input"><i>R$</i><input type="number" min="0" step="100" placeholder="0,00" value={simulation.firstMonthCash} onChange={(event) => setSimulation({ ...simulation, firstMonthCash: event.target.value })} /></span></label>
                                    <label className="commission-field"><span>Base desta parcela</span><span className="commission-affixed-input"><i>R$</i><input type="number" min="0" step="100" placeholder="0,00" value={simulation.commissionBase} onChange={(event) => setSimulation({ ...simulation, commissionBase: event.target.value })} /></span></label>
                                </div>
                                <label className="commission-field commission-date-field"><span>Data da venda</span><input type="date" value={simulation.eventDate} onChange={(event) => setSimulation({ ...simulation, eventDate: event.target.value })} /></label>
                            </div>
                        </form>
                        <aside className="commission-simulation-result">
                            {simulationResult ? (
                                <>
                                    <header><span>Comissão gerada</span><strong>{formatBRL(simulationResult.totalCommission)}</strong><small>total desta parcela para os participantes selecionados</small></header>
                                    <div className="commission-result-context"><span><b>{simulationResult.product.label}</b><small>{formatBRL(simulationResult.contractTotal)} de contrato</small></span><span><b>{simulationResult.cashPercent.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%</b><small>cash collect informado</small></span></div>
                                    <div className="commission-result-lines">
                                        <div><span><strong>{roleLabel(simulationResult.sellerRole)} N{simulationResult.sellerRole.nivel ?? 1}</strong><small>{rateCalculationLabel(simulationResult.seller)} × {formatBRL(simulationResult.commissionBase)}</small></span><b>{formatBRL(simulationResult.seller.totalCommission)}</b></div>
                                        {simulationResult.preSale && <div><span><strong>{roleLabel(simulationResult.preSaleRole)} N{simulationResult.preSaleRole.nivel ?? 1}</strong><small>{rateCalculationLabel(simulationResult.preSale)} × {formatBRL(simulationResult.commissionBase)}</small></span><b>{formatBRL(simulationResult.preSale.totalCommission)}</b></div>}
                                        {simulationResult.management && <div><span><strong>Gestão comercial</strong><small>{rateCalculationLabel(simulationResult.management)} × {formatBRL(simulationResult.commissionBase)}</small></span><b>{formatBRL(simulationResult.management.totalCommission)}</b></div>}
                                    </div>
                                    <div className="commission-tier-progress">
                                        <span>Progresso da entrada</span>
                                        <div><i><b style={{ width: `${Math.min(100, simulationResult.firstMonthCash / Math.max(1, simulationResult.nextTier?.threshold ?? simulationResult.reachedTier?.threshold ?? simulationResult.contractTotal) * 100)}%` }} /></i><strong>{formatBRL(simulationResult.firstMonthCash)}</strong></div>
                                        {simulationResult.nextTier ? <p>Faltam <strong>{formatBRL(Math.max(0, simulationResult.nextTier.threshold - simulationResult.firstMonthCash))}</strong> para a próxima faixa.</p> : simulationResult.reachedTier ? <p><CheckCircle2 size={15} />Maior faixa atingida: bônus de <strong>{formatBRL(simulationResult.reachedTier.rule.value)}</strong>.</p> : <p>Nenhuma faixa progressiva ativa para este produto.</p>}
                                    </div>
                                </>
                            ) : <div className="commission-result-empty"><Calculator size={24} /><strong>{draftValidationErrors.length ? 'O plano tem pendências' : 'Preencha o cenário'}</strong><span>{draftValidationErrors.length ? draftValidationErrors[0] : 'Informe contrato e base da parcela para calcular.'}</span></div>}
                        </aside>
                    </div>
                </section>
            )}

            {dataPanelOpen && (
                <div className="commission-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDataPanelOpen(false); }}>
                    <aside className="commission-data-drawer" role="dialog" aria-modal="true" aria-labelledby="commission-data-title" onKeyDown={trapReviewFocus}>
                        <header><div><span>Rastreabilidade</span><h2 id="commission-data-title">Dados do cálculo</h2></div><button ref={dataCloseRef} type="button" aria-label="Fechar painel" onClick={() => setDataPanelOpen(false)}><X size={19} /></button></header>
                        <div className="commission-data-source"><span>Venda e participantes</span><strong>GoHighLevel → ghl_vendas</strong><small>Cliente, produto, valor comercial, Closer e SDR.</small></div>
                        <div className="commission-data-source"><span>Condições financeiras</span><strong>Desk → venda pendente</strong><small>Contrato, entrada, parcelas, pagamento e gestor informado.</small></div>
                        <div className="commission-data-source"><span>Taxas e regras</span><strong>bd_cargos + app_settings</strong><small>Percentuais, exceções por produto, bônus, faixas e vigência.</small></div>
                        <div className="commission-data-source"><span>Valor final</span><strong>bd_comissoes</strong><small>Somente lançamentos aprovados alimentam a visão financeira do Dashboard.</small></div>
                        <div className="commission-data-warning"><AlertTriangle size={17} /><span><strong>A entrada ainda é informada no Desk.</strong><small>Cash collect não representa conciliação bancária automática nesta versão.</small></span></div>
                    </aside>
                </div>
            )}

            {deleteRequest && (
                <div className="commission-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDeleteRequest(null); }}>
                    <section className="commission-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="commission-delete-title" onKeyDown={trapReviewFocus}>
                        <span className="commission-danger-icon"><Trash2 size={19} /></span><h2 id="commission-delete-title">Excluir configuração?</h2><p><strong>{deleteRequest.label}</strong> será removida do rascunho. O cálculo real só muda depois da publicação.</p>
                        <footer><button type="button" className="commission-button commission-button-quiet" autoFocus onClick={() => setDeleteRequest(null)}>Cancelar</button><button type="button" className="commission-button commission-button-danger-solid" onClick={confirmDelete}>Excluir do rascunho</button></footer>
                    </section>
                </div>
            )}

            {reviewOpen && (
                <div className="commission-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) setReviewOpen(false); }}>
                    <section className="commission-publish-dialog" role="dialog" aria-modal="true" aria-labelledby="commission-publish-title" onKeyDown={trapReviewFocus}>
                        <header><div><span>Revisão final</span><h2 id="commission-publish-title">Publicar alterações</h2><p>Novos lançamentos passam a usar o plano. O histórico não é recalculado.</p></div><button ref={reviewCloseRef} type="button" aria-label="Fechar revisão" onClick={() => setReviewOpen(false)} disabled={saving}><X size={19} /></button></header>
                        <div className="commission-change-summary">
                            <span className={rulesDirty ? 'changed' : ''}>{rulesDirty ? <Check size={15} /> : <i />}<span><strong>Regras e bônus</strong><small>{rulesDirty ? `${rulesConfig.rules.length} configuração(ões) no rascunho` : 'sem alteração'}</small></span></span>
                            <span className={rolesDirty ? 'changed' : ''}>{rolesDirty ? <Check size={15} /> : <i />}<span><strong>Percentuais por cargo</strong><small>{rolesDirty ? `${dirtyRoleIds.size} perfil(is) alterado(s)` : 'sem alteração'}</small></span></span>
                            <span className={productsDirty ? 'changed' : ''}>{productsDirty ? <Check size={15} /> : <i />}<span><strong>Exceções por produto</strong><small>{productsDirty ? 'catálogo será atualizado' : 'sem alteração'}</small></span></span>
                        </div>
                        <div className="commission-publish-warning"><AlertTriangle size={17} /><span><strong>Publicação por etapas</strong><small>Cargos, produtos e regras usam fontes distintas. Se uma etapa falhar, a tela informa o que já foi persistido.</small></span></div>
                        {productsDirty && productInvariants.avisos.length > 0 && <div className="commission-publish-warning"><AlertTriangle size={17} /><span><strong>Catálogo compartilhado com aviso</strong><small>{productInvariants.avisos.map((warning) => warning.resumo).join(' ')}</small></span></div>}
                        <footer><button type="button" className="commission-button commission-button-quiet" onClick={() => setReviewOpen(false)} disabled={saving}>Continuar revisando</button><button type="button" className="commission-button" onClick={publishPlan} disabled={saving}>{saving ? <Loader2 size={16} className="commission-spin" /> : <Save size={16} />}{saving ? 'Publicando…' : 'Publicar plano'}</button></footer>
                    </section>
                </div>
            )}
        </main>
    );
}
